import { useState, useEffect, useCallback } from 'react';
import { Link, Plus, Trash, MagnifyingGlass, CircleNotch, Warning, ArrowClockwise } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AddMappingForm({ onCreated }) {
  const [domain, setDomain] = useState('');
  const [custCode, setCustCode] = useState('');
  const [pfCode, setPfCode] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pickingProduct, setPickingProduct] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!pickingProduct || !pfCode || pfCode.length < 2) {
      setProductResults([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/products?q=${encodeURIComponent(pfCode)}&limit=8`, { credentials: 'include' });
        if (r.ok) {
          const data = await r.json();
          setProductResults(data.products || []);
        }
      } catch { /* ignore */ }
      setSearching(false);
    }, 250);
    return () => clearTimeout(t);
  }, [pfCode, pickingProduct]);

  const submit = async () => {
    setError('');
    if (!domain.trim() || !custCode.trim() || !pfCode.trim()) {
      setError('Alle velden zijn verplicht'); return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API}/article-mappings`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_domain: domain.trim(), customer_article_code: custCode.trim(), poolfix_code: pfCode.trim() }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        setError(e.detail || 'Opslaan mislukt');
      } else {
        setDomain(''); setCustCode(''); setPfCode('');
        onCreated?.();
      }
    } catch { setError('Verbindingsfout'); }
    setSaving(false);
  };

  return (
    <div className="border border-zinc-200 rounded-sm p-4 mb-4 bg-zinc-50/40" data-testid="add-mapping-form">
      <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Nieuwe mapping toevoegen</div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <input
          value={domain} onChange={e => setDomain(e.target.value)}
          placeholder="Klant domein (bv. variopool.nl)"
          className="px-3 py-2 text-sm border border-zinc-200 rounded-sm"
        />
        <input
          value={custCode} onChange={e => setCustCode(e.target.value)}
          placeholder="Klantcode (bv. RE-D933-1-4529-16x160)"
          className="px-3 py-2 text-sm border border-zinc-200 rounded-sm font-data"
        />
        <div className="relative">
          <input
            value={pfCode} onChange={e => setPfCode(e.target.value)}
            onFocus={() => setPickingProduct(true)}
            placeholder="PoolFIX artikelcode (zoek/kies)"
            className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm font-data"
            data-testid="pf-code-input"
          />
          {pickingProduct && pfCode && productResults.length > 0 && (
            <div className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-zinc-200 rounded-sm shadow-lg max-h-64 overflow-y-auto">
              {searching && <div className="px-3 py-2 text-xs text-zinc-400">Zoeken...</div>}
              {productResults.map(p => (
                <button
                  key={p.code} type="button"
                  onClick={() => { setPfCode(p.code); setPickingProduct(false); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 border-b border-zinc-50 last:border-0"
                >
                  <div className="font-data font-bold text-zinc-800">{p.code}</div>
                  <div className="text-zinc-600 truncate">{p.description}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={submit} disabled={saving}
          data-testid="save-mapping-btn"
          className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-sm hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-1"
        >
          {saving ? <CircleNotch size={12} className="animate-spin" /> : <Plus size={12} />} Opslaan
        </button>
      </div>
      {error && <div className="mt-2 text-xs text-red-700"><Warning size={12} className="inline mr-1" /> {error}</div>}
    </div>
  );
}

export default function ArticleMappingsPage() {
  const [data, setData] = useState({ mappings: [], domains: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [domainFilter, setDomainFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/article-mappings${domainFilter ? `?domain=${encodeURIComponent(domainFilter)}` : ''}`;
      const r = await fetch(url, { credentials: 'include' });
      if (r.ok) setData(await r.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [domainFilter]);

  useEffect(() => { load(); }, [load]);

  const remove = async (m) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Verwijder mapping ${m.customer_domain} → ${m.customer_article_code}?`)) return;
    try {
      await fetch(`${API}/article-mappings?domain=${encodeURIComponent(m.customer_domain)}&customer_article_code=${encodeURIComponent(m.customer_article_code)}`, {
        method: 'DELETE', credentials: 'include',
      });
      load();
    } catch { /* ignore */ }
  };

  const filtered = (data.mappings || []).filter(m => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (m.customer_article_code || '').toLowerCase().includes(q)
      || (m.poolfix_code || '').toLowerCase().includes(q)
      || (m.poolfix_description || '').toLowerCase().includes(q);
  });

  return (
    <div className="animate-fade-in" data-testid="article-mappings-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight flex items-center gap-2">
            <Link size={30} weight="duotone" className="text-indigo-600" />
            Klant-artikel mappings
          </h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Per klant-domein leg je vast welke klant-artikelcode bij welk PoolFIX artikel hoort.
            Zodra een mapping bestaat past de AI hem automatisch toe op nieuwe orders van dezelfde klant —
            zo verdwijnen de gele "Geen match" rijen na een paar correcties.
          </p>
        </div>
        <button onClick={load} className="px-3 py-1.5 text-sm border border-zinc-200 rounded-sm hover:bg-zinc-50 flex items-center gap-1">
          <ArrowClockwise size={13} /> Vernieuw
        </button>
      </div>

      <AddMappingForm onCreated={load} />

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <select
          value={domainFilter}
          onChange={e => setDomainFilter(e.target.value)}
          data-testid="domain-filter"
          className="px-3 py-1.5 text-sm border border-zinc-200 rounded-sm bg-white"
        >
          <option value="">Alle klant-domeinen ({data.domains?.length || 0})</option>
          {(data.domains || []).map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Zoek klantcode of PoolFIX artikel..."
            className="w-full pl-7 pr-3 py-1.5 text-sm border border-zinc-200 rounded-sm"
          />
        </div>
        <span className="text-xs text-zinc-500 ml-auto">{filtered.length} mapping{filtered.length === 1 ? '' : 's'}</span>
      </div>

      <div className="border border-zinc-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="mappings-table">
          <thead className="bg-zinc-50">
            <tr>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Klant-domein</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Klantcode</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">→ PoolFIX</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Omschrijving</th>
              <th className="px-3 py-2 text-center text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Gebruikt</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Bron</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center"><CircleNotch size={18} className="animate-spin inline mr-2" /> Laden...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-zinc-400 text-xs">Geen mappings — voeg er één toe of laat de AI ze automatisch leren wanneer je in de inbox handmatig een artikel aan een ongematchte regel koppelt.</td></tr>
            ) : filtered.map(m => (
              <tr key={`${m.customer_domain}-${m.customer_article_code}`} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`mapping-row-${m.mapping_id}`}>
                <td className="px-3 py-2 font-data text-zinc-700">{m.customer_domain}</td>
                <td className="px-3 py-2 font-data text-zinc-800">{m.customer_article_code}</td>
                <td className="px-3 py-2 font-data text-indigo-700 font-semibold">{m.poolfix_code}</td>
                <td className="px-3 py-2 text-zinc-600 text-xs">{m.poolfix_description || '—'}</td>
                <td className="px-3 py-2 text-center font-data">{m.used_count || 0}×</td>
                <td className="px-3 py-2 text-[10px]">
                  {m.auto_learned ? (
                    <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-sm uppercase tracking-wider font-bold">Auto-geleerd</span>
                  ) : (
                    <span className="px-1.5 py-0.5 bg-zinc-100 text-zinc-600 rounded-sm uppercase tracking-wider font-bold">Handmatig</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => remove(m)} className="p-1 hover:bg-red-50 rounded-sm" data-testid={`delete-mapping-${m.mapping_id}`}>
                    <Trash size={13} className="text-zinc-400 hover:text-red-500" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
